import React, { useCallback, useEffect, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import Link from 'antd/es/typography/Link'
import { Table } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { AIChatSessionSearchResultFragment } from 'types/graphql'

import { useSearchAIChatSessionsQuery } from 'src/fetch/aiChatSessions'
import AIChatSessionItem from 'src/pages/AIAssistantHistoryPage/AIChatSessionItem'

type Props = {
  onRowClick?: (sessionId: number) => void
  activeSessionId: number | null
}

const AIChatSessions = ({ activeSessionId, onRowClick }: Props) => {
  const getRowId = useCallback(
    (result: AIChatSessionSearchResultFragment) => `${result.session.id}`,
    []
  )

  const { debouncedSearchQuery, ...tableProps } = useTableState<AIChatSessionSearchResultFragment>({
    initialState: { pagination: { pageIndex: 0, pageSize: 25 } },
  })
  const { results, count, isFetching } = useSearchAIChatSessionsQuery({
    page: tableProps.pagination.pageIndex + 1,
    pageSize: tableProps.pagination.pageSize,
    query: debouncedSearchQuery,
  })

  useEffect(() => {
    if (activeSessionId) {
      return
    }
    if (results.length > 0) {
      const sessionId = results[0]?.session?.id
      if (sessionId) {
        onRowClick?.(sessionId)
      }
    }
  }, [results, onRowClick, activeSessionId])

  const handleRowClick = useCallback(
    (result: AIChatSessionSearchResultFragment) => {
      onRowClick?.(result.session.id)
    },
    [onRowClick]
  )

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AIChatSessionSearchResultFragment>()
    return [
      columnHelper.display({
        id: 'conversation',
        cell: (info) => {
          const { session, chat } = info.row.original
          return (
            <>
              <Link
                onClick={(e) => {
                  e.preventDefault()
                  handleRowClick(info.row.original)
                }}
              >
                <AIChatSessionItem session={session} chat={chat} />
              </Link>
            </>
          )
        },
      }),
    ]
  }, [handleRowClick])

  const getRowIsHighlighted = useCallback(
    (row: AIChatSessionSearchResultFragment) => row.session.id === activeSessionId,
    [activeSessionId]
  )

  return (
    <Table
      columns={columns}
      data={results}
      enablePagination
      enableSearching
      getRowId={getRowId}
      isLoading={isFetching}
      totalCount={count}
      shouldHideHeader
      shouldHideEmptyStateImage
      getRowIsHighlighted={getRowIsHighlighted}
      {...tableProps}
    />
  )
}

export default React.memo(AIChatSessions)
